/*
 * Copyright (c) 2018-2023 MemberCenter, a subsidiary of Cameron Collective
 *   All Rights Reserved
 */
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import { useMemo } from 'react';

import { useGetMembercenterPlansQuery, useGetUserQuery, useGetUserStatsQuery } from 'store/api/version-api.slice';

import { styles } from './styles';

const SubscriptionInformation = () => {
	const { data: membercenterPlansResponse = {}, isLoading: isMembercenterPlansLoading } =
		useGetMembercenterPlansQuery();

	const memberCenterPlans = useMemo(
		() => membercenterPlansResponse.results || [],
		[membercenterPlansResponse.results]
	);

	const { data: userInstance = {}, isLoading: isUserDataFetching } = useGetUserQuery();
	const { data: statsResponse = {}, isLoading: isStatsLoading } = useGetUserStatsQuery();

	const stats = useMemo(() => statsResponse.stats ?? {}, [statsResponse.stats]);

	const selectedPlan = useMemo(() => {
		if (userInstance.plan && memberCenterPlans) {
			const plan = memberCenterPlans.find(_plan => _plan.id === userInstance.plan);

			return plan ? `${plan.name} ${plan.amount} ${plan.amount_currency}` : '-';
		}
		return '-';
	}, [userInstance.plan, memberCenterPlans]);

	const contactsInUse = useMemo(() => {
		return `${stats?.num_activemembers ?? 0} of ${stats?.plan_limit ?? 0}`;
	}, [stats]);

	const endDate = useMemo(() => {
		if (userInstance) {
			const notTrial = userInstance.renewal_date != null;

			return notTrial ? userInstance.renewal_date : userInstance.trial_end_date;
		}

		return '';
	}, [userInstance]);

	if (isMembercenterPlansLoading || isUserDataFetching || isStatsLoading)
		return (
			<Paper elevation={2} sx={styles.paper}>
				<Box sx={styles.centeredSubscription}>
					<CircularProgress size={56} sx={styles.fabProgress} />
				</Box>
			</Paper>
		);

	return (
		<Paper elevation={2} sx={styles.paper}>
			<Toolbar>
				<Typography component={'span'} variant="h6" color="primary" sx={styles.title}>
					Subscription Information
				</Typography>
			</Toolbar>
			<Divider />
			<List sx={styles.listWrapper}>
				<Box sx={styles.listRow}>
					<ListItemText secondary={'Plan Type:'} />
					<ListItemText secondary={selectedPlan} />
				</Box>
				<Box sx={styles.listRow}>
					<ListItemText secondary={'Members Used:'} />
					<ListItemText secondary={contactsInUse} />
				</Box>
				<Box sx={styles.listRow}>
					<ListItemText secondary={userInstance.plan ? 'Renewal Date:' : 'Trial Period:'} />
					<ListItemText secondary={endDate} />
				</Box>
			</List>
		</Paper>
	);
};

export default SubscriptionInformation;
